<script setup lang="ts">
  import { MpLoadingContainer, MpText } from '@borg/ui';

  const route = useRoute();
  const { t } = useI18n();

  const tag = computed(() => route.query.tag as string);
  const category = computed(() => route.query.category as string);

  // Create descriptive title based on search type
  const pageTitle = computed(() => {
    if (tag.value) {
      return decodeURI(tag.value);
    }
    if (category.value) {
      return decodeURI(category.value);
    }
    return t('ADVICES_PAGE.TITLE');
  });

  // Watch for route changes and update page title dynamically
  watch(
    [tag, category],
    () => {
      const title = pageTitle.value;
      const description = t('ADVICES_PAGE.DESCRIPTION');

      usePageHead({
        pageName: title,
        description: description,
      });
    },
    { immediate: true }
  );

  const { data: articles, pending } = await articlesSearchRepository.getMany({
    tag,
    category,
    query: ref(''), // Empty query since there's no search UI
  });
</script>

<template>
  <div class="mp-articles">
    <main>
      <MpText
        as="h1"
        type="h2"
        class="mp-articles__title"
      >
        <template v-if="tag">
          {{ decodeURI(tag) }}
        </template>
        <template v-else-if="category">
          {{ decodeURI(category) }}
        </template>
        <template v-else>
          {{ $t('ADVICES_PAGE.TITLE') }}
        </template>
      </MpText>
      <MpLoadingContainer
        :is-loading="pending"
        :has-error="!articles || articles.length === 0"
      >
        <ArticleList
          :articles="articles ?? []"
          :columns="2"
        />
      </MpLoadingContainer>
    </main>
    <aside>
      <TopicsAndArticles />
    </aside>
  </div>
</template>

<style scoped lang="scss">
  .mp-articles {
    display: flex;
    flex-direction: column;
    gap: var(--mp-space-70);

    &__title {
      margin-bottom: var(--mp-space-60);
    }

    main {
      display: flex;
      flex-direction: column;
    }

    aside {
      border: 1px solid var(--mp-color-border-c-10);
      border-radius: 15px;
      padding: var(--mp-space-70);
      height: 100%;
    }

    @include screen-md {
      flex-direction: row;
      main {
        width: 46rem;
      }
      aside {
        width: 22rem;
      }
    }
  }
</style>
